"use client";

import * as React from "react";
import { SwipeableItem } from "@/components/ui/SwipeableItem";
import { PlusSVG, TrashCanSVG } from "@/components/ui/SVG";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { BlockBase } from "@/cms/blocks/registry/types";
import type { ConfirmInvoker } from "../types";
import {
  getBlockLocaleStrings,
  getLocaleUI,
  insertAtEnd,
  removeAt,
  updateAt,
} from "../utils";

export function ListEditor({
  block,
  onChange,
  onRequestConfirm,
  locale,
}: {
  block: BlockBase;
  onChange: (patch: (data: any) => any) => void;
  onRequestConfirm: ConfirmInvoker;
  locale: "en" | "fa";
}) {
  const data = block.data as any;
  const items: string[] = React.useMemo(
    () => (Array.isArray(data.items) ? data.items : []),
    [data.items],
  );
  const localeStrings = React.useMemo(
    () => getBlockLocaleStrings(locale),
    [locale],
  );
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { dir: inputDir, inputClass: localizedInputClass } =
    getLocaleUI(locale);
  const itemRefs = React.useRef<Array<HTMLTextAreaElement | null>>([]);
  const pendingFocusIndex = React.useRef<number | null>(null);

  const autoResizeTextarea = React.useCallback((element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = "0px";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  const toggleOrdered = (checked: boolean) => {
    onChange((prev) => ({ ...prev, ordered: checked }));
  };

  const addItem = () => {
    pendingFocusIndex.current = items.length;
    onChange((prev) => {
      const prevItems = Array.isArray(prev.items) ? prev.items : [];
      return { ...prev, items: insertAtEnd(prevItems, "") };
    });
  };

  const updateItem = (index: number, value: string) => {
    onChange((prev) => {
      const prevItems = Array.isArray(prev.items) ? prev.items : [];
      return {
        ...prev,
        items: updateAt(prevItems, index, () => value),
      };
    });
  };

  const removeItem = (index: number) => {
    const commit = () => {
      onChange((prev) => {
        const prevItems = Array.isArray(prev.items) ? prev.items : [];
        return { ...prev, items: removeAt(prevItems, index) };
      });
    };

    onRequestConfirm(
      {
        title: "Remove list item",
        description: "Are you sure you want to delete this list item?",
        confirmLabel: "Remove",
        variant: "danger",
      },
      commit,
    );
  };

  React.useEffect(() => {
    const targetIndex = pendingFocusIndex.current;
    if (targetIndex === null) return;
    const target = itemRefs.current[targetIndex];
    if (target) {
      autoResizeTextarea(target);
      target.focus();
      target.select();
      pendingFocusIndex.current = null;
    }
  }, [autoResizeTextarea, items]);

  React.useEffect(() => {
    itemRefs.current.forEach((element) => autoResizeTextarea(element));
  }, [autoResizeTextarea, items]);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((value, index) => (
          <SwipeableItem
            key={`${block.id}-item-${index}`}
            disabled={isDesktop}
            rightActions={isDesktop ? undefined : (
              <button
                type="button"
                className=" rounded bg-red-600 p-2 ml-6 hover:bg-red-500 active:bg-red-700"
                onClick={() => removeItem(index)}
              >
                <TrashCanSVG className="w-4" svgColor="white" />
              </button>
            )}
            actionWidth="auto"
          >
            <div className="group relative flex items-stretch gap-2 bg-white">
              <textarea
                dir={inputDir}
                rows={1}
                className={`flex-1 resize-none overflow-hidden [overflow-wrap:anywhere] ${localizedInputClass} ${inputDir === "rtl" ? "md:pl-12" : "md:pr-12"}`}
                placeholder={localeStrings.placeholders.listItem(index)}
                value={value}
                onChange={(e) => {
                  updateItem(index, e.target.value);
                  autoResizeTextarea(e.currentTarget);
                }}
                ref={(element) => {
                  itemRefs.current[index] = element;
                  autoResizeTextarea(element);
                }}
                onPointerDownCapture={(e) => {
                  (e.currentTarget as HTMLTextAreaElement).style.touchAction =
                    "manipulation";
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLTextAreaElement).style.touchAction =
                    "";
                }}
              />
              <button
                type="button"
                onClick={() => removeItem(index)}
                className={`absolute top-2 opacity-0 pointer-events-none transition-opacity  cursor-pointer active:bg-red-700 md:group-hover:opacity-100 md:group-hover:pointer-events-auto ${
                  inputDir === "rtl" ? "left-3" : "right-3"
                } hidden md:block`}
                aria-label="Remove list item"
              >
                <TrashCanSVG className="w-3" svgColor="var(--color-red-600)" accentColor="var(--color-red-600)" />
              </button>
            </div>
          </SwipeableItem>
        ))}
        {items.length === 0 && (
          <div className="text-sm text-gray-500">
            {localeStrings.helpers.noItems}
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!data.ordered}
            onChange={(event) => toggleOrdered(event.target.checked)}
          />
          {localeStrings.helpers.ordered}
        </label>
        <button
          type="button"
          className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer"
          onClick={addItem}
        >
          <PlusSVG
            className="bg-slateCustom h-5 w-5 rounded p-1"
            svgColor="white"
          />
          {localeStrings.helpers.addItem}
        </button>
      </div>
    </div>
  );
}
